import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, data: null });
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async (n) => {
    if (!sessionId) { setState({ status: "error", data: { message: "Missing session." } }); return; }
    if (n >= 12) { setState({ status: "timeout" }); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setState({ status: "paid", data: res.data });
        return;
      }
      if (res.data.status === "expired") {
        setState({ status: "expired" });
        return;
      }
      setTimeout(() => setAttempts((a) => a + 1), 2500);
    } catch (e) {
      setTimeout(() => setAttempts((a) => a + 1), 3000);
    }
  }, [sessionId]);

  useEffect(() => { poll(attempts); }, [attempts, poll]);

  return (
    <main className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6" data-testid="payment-status-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
        className="max-w-xl w-full text-center py-24">
        {state.status === "checking" && (
          <>
            <Loader2 size={28} className="mx-auto mb-8 text-[#C5A059] animate-spin" strokeWidth={1.5} />
            <p className="eyebrow text-[#C5A059] mb-6">Confirming Payment</p>
            <h1 className="font-serif-editorial text-4xl md:text-5xl italic mb-4">One moment…</h1>
            <p className="text-[#5C5C5C]">Verifying with Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#C5A059]/15 flex items-center justify-center mb-8">
              <Check size={22} strokeWidth={1.5} className="text-[#C5A059]" />
            </div>
            <p className="eyebrow text-[#C5A059] mb-6">Payment Confirmed</p>
            <h1 className="font-serif-editorial text-4xl md:text-5xl mb-4">Thank you.</h1>
            <p className="text-[#5C5C5C] mb-10 leading-relaxed">
              We received ${state.data?.amount} {(state.data?.currency || "usd").toUpperCase()}. Your concierge is finalizing the itinerary and will reach out within 24 to 72 hours.
            </p>
            <Link to="/" data-testid="btn-home" className="ee-btn-primary">Return Home</Link>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E6C5C0] flex items-center justify-center mb-8">
              <X size={22} strokeWidth={1.5} className="text-[#1A1A1A]" />
            </div>
            <p className="eyebrow text-[#1A1A1A] mb-6">{state.status === "timeout" ? "Still checking" : "Session issue"}</p>
            <h1 className="font-serif-editorial text-4xl md:text-5xl italic mb-4">Let&rsquo;s try that again.</h1>
            <p className="text-[#5C5C5C] mb-10">{state.status === "timeout" ? "Payment is still processing. Check your email for confirmation." : "Something interrupted the session. Return to the inquiry to retry."}</p>
            <Link to="/#inquiry" data-testid="btn-back" className="ee-btn-primary">Back to Inquiry</Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
